/**
 * Pulse — AI dashboard briefing widget (Task #2361).
 *
 * Renders at the top of the host dashboard. Greets the host by name, lists
 * 3–8 prioritized items from the Gemini-backed `/api/app/pulse/briefing`
 * endpoint, with deep links into existing flows. Read-only for host data —
 * Sprint 1+ also POSTs lifecycle signals back to `/api/app/pulse/*`
 * (view marker, feedback, snooze, dismiss, settings).
 *
 * Task #2426 additions:
 *   - 403 handling: shows a compact "upgrade to Professional" card instead of
 *     the full widget when the account is below the Professional tier.
 *   - 429 handling: the Refresh button shows a live countdown and is disabled
 *     until the tier-based cooldown expires.
 *   - Viewer-personalized items from the server carry an `assignedToViewer`
 *     evidence flag and appear at the top of the list.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Eye,
  RefreshCw,
  X,
  Inbox,
  Star,
  TrendingUp,
  Target,
  DollarSign,
  FileBarChart2,
  ArrowRight,
  HelpCircle,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Settings,
  CalendarCheck,
  FileText,
  ClipboardList,
  BrainCircuit,
  Check,
  Lock,
  Volume2,
  Pause,
  Play,
  RotateCcw,
  AlertCircle,
  Package,
} from "lucide-react";
import { PULSE_SAMPLE_ITEMS } from "@shared/pulse-sample";

type PulsePriority = "high" | "medium" | "low";
type PulseCategory =
  | "Inbox"
  | "Reviews"
  | "Pipeline"
  | "Buy Box"
  | "Financials"
  | "PadSplit Reports"
  | "Booking Requests"
  | "Forms"
  | "Tasks"
  | "Memory";

interface PulseItem {
  itemKey: string;
  headline: string;
  whyItMatters: string;
  priority: PulsePriority;
  category: PulseCategory;
  deepLinkPath: string;
  secondaryAction?: { label: string; path: string } | null;
  evidence?: Record<string, unknown> | null;
}

export interface PulseBriefing {
  greeting: string;
  generatedAt: string;
  source: "gemini" | "fallback";
  items: PulseItem[];
  // Task #2431 — server-persisted manual-refresh cooldown remaining.
  // Surfaced on both GET /briefing and POST /briefing/refresh so the
  // countdown survives page reloads.
  refreshCooldownSeconds?: number;
}

interface PulseVoiceOption {
  id: string;
  label: string;
  description: string;
}

interface PulseSettings {
  emailDigestEnabled: boolean;
  lastEmailSentDate: string | null;
  digestSendHour: number;
  lastDigestStatus: string | null;
  lastDigestAttemptAt: string | null;
  preferredVoiceId: string;
  voiceOptions: PulseVoiceOption[];
}

const CATEGORY_ICON: Record<PulseCategory, React.ComponentType<{ className?: string }>> = {
  Inbox: Inbox,
  Reviews: Star,
  Pipeline: TrendingUp,
  "Buy Box": Target,
  Financials: DollarSign,
  "PadSplit Reports": FileBarChart2,
  "Booking Requests": CalendarCheck,
  Forms: FileText,
  Tasks: ClipboardList,
  Memory: BrainCircuit,
};

const PRIORITY_STYLES: Record<PulsePriority, string> = {
  high: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700/40",
  medium: "bg-brand-accent/15 text-brand-amber border-brand-accent/30 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700/40",
  low: "bg-brand-muted text-brand border-brand/20 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700/40",
};

const DISMISS_KEY = "pulse-briefing-dismissed";
export const PULSE_BETA_KEY = "pulse_dashboard_briefing";

/** Parse retryAfterSeconds from a 429 apiRequest error. */
function parseRetryAfterSeconds(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (!match || Number(match[1]) !== 429) return null;
  try {
    const parsed = JSON.parse(match[2]);
    if (typeof parsed?.retryAfterSeconds === "number" && parsed.retryAfterSeconds > 0) {
      return parsed.retryAfterSeconds;
    }
  } catch { /* ignore */ }
  return null;
}

/** Returns true if an apiRequest error was a 403 (tier gate). */
function is403Error(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.message.startsWith("403:");
}

function formatEvidenceValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value.every((v) => typeof v === "string" || typeof v === "number")) {
      return value.join(", ");
    }
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[object]";
    }
  }
  return String(value);
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// Internal/control evidence keys that must never be shown as plain text in the
// "Why this" popover. `assignedToViewer` is a render flag; `suggestionId` is the
// KB-suggestion control id the Yes/No buttons read (Task #3974 — it must not
// surface as primary context). Any key ending in "Link" is rendered as a real
// clickable anchor instead of a raw path string.
const HIDDEN_EVIDENCE_KEYS = new Set(["assignedToViewer", "suggestionId"]);

function WhyThisButton({ item }: { item: PulseItem }) {
  const entries = item.evidence
    ? Object.entries(item.evidence).filter(([k]) => !HIDDEN_EVIDENCE_KEYS.has(k))
    : [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand dark:hover:text-blue-400"
          data-testid={`button-pulse-why-${item.itemKey}`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Why this?
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 text-xs"
        data-testid={`popover-pulse-why-${item.itemKey}`}
      >
        <div className="font-semibold mb-2 text-brand dark:text-blue-400">Why this item?</div>
        {entries.length === 0 ? (
          <p className="text-muted-foreground">
            Pulse surfaced this from the underlying signal feed. No additional
            structured evidence is attached.
          </p>
        ) : (
          <dl className="space-y-1.5">
            {entries.map(([k, v]) => {
              const isLink = k.endsWith("Link") && typeof v === "string" && v.startsWith("/");
              return (
                <div key={k} className="flex flex-col">
                  <dt className="font-medium text-foreground">{humanizeKey(k)}</dt>
                  <dd className="text-muted-foreground break-words">
                    {isLink ? (
                      <Link
                        href={v as string}
                        className="text-brand dark:text-blue-400 hover:underline"
                        data-testid={`link-pulse-evidence-${k}-${item.itemKey}`}
                      >
                        Jump to source
                      </Link>
                    ) : (
                      formatEvidenceValue(v)
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SettingsPopover() {
  const settingsQuery = useQuery<PulseSettings>({
    queryKey: ["/api/app/pulse/settings"],
    staleTime: 60 * 1000,
  });
  const emailMutation = useMutation({
    mutationFn: async (emailDigestEnabled: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/pulse/settings", {
        emailDigestEnabled,
      });
      return (await res.json()) as { emailDigestEnabled: boolean; digestSendHour: number; preferredVoiceId: string };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PulseSettings>(
        ["/api/app/pulse/settings"],
        (prev) => prev ? { ...prev, emailDigestEnabled: data.emailDigestEnabled } : prev,
      );
    },
  });
  const digestHourMutation = useMutation({
    mutationFn: async (digestSendHour: number) => {
      const res = await apiRequest("PATCH", "/api/app/pulse/settings", { digestSendHour });
      return (await res.json()) as { ok: boolean; digestSendHour: number };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PulseSettings>(
        ["/api/app/pulse/settings"],
        (prev) => prev ? { ...prev, digestSendHour: data.digestSendHour } : prev,
      );
    },
  });
  const voiceMutation = useMutation({
    mutationFn: async (preferredVoiceId: string) => {
      const res = await apiRequest("PATCH", "/api/app/pulse/settings", {
        preferredVoiceId,
      });
      return (await res.json()) as { emailDigestEnabled: boolean; preferredVoiceId: string };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<PulseSettings>(
        ["/api/app/pulse/settings"],
        (prev) => prev ? { ...prev, preferredVoiceId: data.preferredVoiceId } : prev,
      );
    },
  });
  // Task #2447 — single shared <audio> for voice previews so tapping a new
  // sample stops the previous one rather than overlapping playback.
  const sampleAudioRef = useRef<HTMLAudioElement | null>(null);
  const [samplePlayingId, setSamplePlayingId] = useState<string | null>(null);
  const [sampleErrorId, setSampleErrorId] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      const a = sampleAudioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);
  function playSample(voiceId: string) {
    setSampleErrorId(null);
    const existing = sampleAudioRef.current;
    if (existing) {
      existing.pause();
      existing.src = "";
    }
    if (samplePlayingId === voiceId) {
      setSamplePlayingId(null);
      return;
    }
    const audio = new Audio(`/api/app/pulse/voice-sample/${encodeURIComponent(voiceId)}`);
    sampleAudioRef.current = audio;
    audio.onended = () => setSamplePlayingId((prev) => (prev === voiceId ? null : prev));
    audio.onerror = () => {
      setSamplePlayingId((prev) => (prev === voiceId ? null : prev));
      setSampleErrorId(voiceId);
    };
    setSamplePlayingId(voiceId);
    audio.play().catch(() => {
      setSamplePlayingId((prev) => (prev === voiceId ? null : prev));
      setSampleErrorId(voiceId);
    });
  }
  const enabled = settingsQuery.data?.emailDigestEnabled ?? false;
  const digestSendHour = settingsQuery.data?.digestSendHour ?? 7;
  const lastDigestStatus = settingsQuery.data?.lastDigestStatus ?? null;
  const voiceOptions = settingsQuery.data?.voiceOptions ?? [];
  const selectedVoiceId = settingsQuery.data?.preferredVoiceId ?? "";

  function formatHour(h: number): string {
    if (h === 0) return "12:00 AM";
    if (h < 12) return `${h}:00 AM`;
    if (h === 12) return "12:00 PM";
    return `${h - 12}:00 PM`;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Pulse settings"
          data-testid="button-pulse-settings"
          data-tour="pulse-settings-trigger"
        >
          <Settings className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80"
        data-testid="popover-pulse-settings"
      >
        <div className="text-sm font-semibold text-brand dark:text-blue-400 mb-3">Pulse settings</div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Daily email digest</p>
            <p className="text-xs text-muted-foreground">
              Get a daily summary of what Pulse thinks you should know.
            </p>
            {lastDigestStatus && (
              <p className={`text-xs mt-0.5 ${lastDigestStatus === "sent" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                data-testid="text-pulse-last-digest-status">
                Last: {lastDigestStatus === "sent" ? "✓ delivered" : `✗ ${lastDigestStatus}`}
              </p>
            )}
          </div>
          <Switch
            checked={enabled}
            disabled={settingsQuery.isLoading || emailMutation.isPending}
            onCheckedChange={(v) => emailMutation.mutate(Boolean(v))}
            data-testid="switch-pulse-email-digest"
          />
        </div>
        {enabled && (
          <div className="mt-3 flex items-center gap-2">
            <label htmlFor="digest-send-hour" className="text-xs text-muted-foreground whitespace-nowrap">
              Send at
            </label>
            <select
              id="digest-send-hour"
              value={digestSendHour}
              disabled={digestHourMutation.isPending}
              onChange={(e) => digestHourMutation.mutate(Number(e.target.value))}
              className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground disabled:opacity-50"
              data-testid="select-pulse-digest-hour"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{formatHour(h)}</option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">local time</span>
          </div>
        )}
        <div className="mt-4 border-t pt-3">
          <p className="text-sm font-medium text-foreground">Voice briefing voice</p>
          <p className="text-xs text-muted-foreground mb-2">
            Pick the voice used when you tap “Tell it to me.” Tap the play
            button next to a voice to preview it.
          </p>
          {voiceOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (
            <ul className="space-y-1" data-testid="list-pulse-voices">
              {voiceOptions.map((v) => {
                const isSelected = v.id === selectedVoiceId;
                const isPlaying = samplePlayingId === v.id;
                const hasError = sampleErrorId === v.id;
                return (
                  <li
                    key={v.id}
                    className={
                      "flex items-start gap-2 rounded-md border px-2 py-1.5 text-sm "
                      + (isSelected
                        ? "border-brand/40 bg-brand-muted/40 dark:border-blue-600/40 dark:bg-blue-900/25"
                        : "border-transparent hover:bg-muted/50")
                    }
                    data-testid={`row-pulse-voice-${v.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => playSample(v.id)}
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand/30 text-brand hover:bg-brand hover:text-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:border-blue-600/40 dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white"
                      aria-label={isPlaying ? `Stop ${v.label} sample` : `Preview ${v.label} voice`}
                      data-testid={`button-pulse-voice-sample-${v.id}`}
                    >
                      {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isSelected && !voiceMutation.isPending) {
                          voiceMutation.mutate(v.id);
                        }
                      }}
                      disabled={voiceMutation.isPending}
                      className="min-w-0 flex-1 text-left disabled:opacity-60"
                      data-testid={`button-pulse-voice-select-${v.id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground truncate">{v.label}</span>
                        {isSelected && (
                          <Check
                            className="h-3.5 w-3.5 text-brand dark:text-blue-400"
                            data-testid={`icon-pulse-voice-selected-${v.id}`}
                          />
                        )}
                      </div>
                      <p
                        className="text-xs text-muted-foreground"
                        data-testid={`text-pulse-voice-description-${v.id}`}
                      >
                        {v.description}
                      </p>
                      {hasError && (
                        <p
                          className="text-xs text-red-600 dark:text-red-400"
                          data-testid={`text-pulse-voice-sample-error-${v.id}`}
                        >
                          Couldn't play sample. Try again in a moment.
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Sample readout shown to accounts whose tier does not include Pulse AI.
 * Everyone — on any tier — can see what their Daily Pulse would look like, with
 * a non-functional "Tell it to me" button and a prominent upgrade CTA. This is
 * intentionally a marketing surface: Pulse is one of the biggest reasons to
 * upgrade to Professional, so lower tiers get a realistic preview rather than a
 * locked-out blank.
 */
export function PulseSamplePreview() {
  // Sample audio playback — fetches a short fixed spoken briefing derived from
  // the same PULSE_SAMPLE_ITEMS rendered below. Not tier-gated server-side, so
  // lower-tier hosts can hear a taste of the voice briefing.
  const [sampleAudioState, setSampleAudioState] = useState<
    "idle" | "loading" | "playing" | "error"
  >("idle");
  const sampleAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      const a = sampleAudioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);

  async function handlePlaySampleBriefing() {
    // Toggle: if it's currently playing or loading, stop and reset.
    if (sampleAudioState === "playing" || sampleAudioState === "loading") {
      const a = sampleAudioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
      setSampleAudioState("idle");
      return;
    }
    setSampleAudioState("loading");
    if (!sampleAudioRef.current) {
      sampleAudioRef.current = new Audio();
    }
    const audio = sampleAudioRef.current;
    audio.src = "/api/app/pulse/sample-briefing/audio";
    audio.onended = () => setSampleAudioState("idle");
    audio.onerror = () => setSampleAudioState("error");
    try {
      await audio.play();
      setSampleAudioState("playing");
    } catch {
      setSampleAudioState("error");
    }
  }

  return (
    <Card
      className="border-brand/20 bg-gradient-to-br from-brand-muted via-white to-white shadow-sm dark:border-blue-700/30 dark:from-blue-950/50 dark:via-card dark:to-card"
      data-testid="card-pulse-sample"
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3" data-tour="pulse-heading">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brand-accent">
              <Eye className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold tracking-wide text-brand dark:text-blue-400">PULSE</span>
                <Badge variant="outline" className="text-[10px] gap-1" data-testid="badge-pulse-sample">
                  Sample
                </Badge>
              </div>
              <p
                className="mt-1 text-base sm:text-lg font-medium text-foreground"
                data-testid="text-pulse-sample-greeting"
              >
                Good morning — here's a taste of what Pulse watches for you.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ExplainerVideoButton pageKey="daily-pulse" compactOnMobile />
            <GuideButton pageKey="daily-pulse-sample" compactOnMobile />
          </div>
        </div>

        {/* Functional sample "Tell it to me" button — plays a short spoken
            briefing derived from the sample items below. Styled in the gold
            brand accent to distinguish it from the real (blue) Professional
            player. */}
        <div className="mt-4">
          <button
            type="button"
            onClick={handlePlaySampleBriefing}
            data-testid="button-pulse-sample-tell-it-to-me"
            data-tour="pulse-tell-it-to-me"
            className="inline-flex items-center gap-2 rounded-full bg-brand-accent px-5 py-2 text-sm font-semibold text-black shadow-sm transition-all hover:bg-brand-accent-hover hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-1"
          >
            {sampleAudioState === "loading" ? (
              <RefreshCw className="h-4 w-4 animate-spin text-black" />
            ) : sampleAudioState === "playing" ? (
              <Pause className="h-4 w-4 text-black" />
            ) : (
              <Volume2 className="h-4 w-4 text-black" />
            )}
            Tell it to me - sample
          </button>
          {sampleAudioState === "error" && (
            <p
              className="mt-1.5 text-xs text-muted-foreground"
              data-testid="text-pulse-sample-audio-error"
            >
              Sample audio is temporarily unavailable. Please try again.
            </p>
          )}
        </div>

        {/* Sample readout */}
        <ul
          className="mt-4 space-y-2"
          data-testid="container-pulse-sample-items"
          data-tour="pulse-sample-items"
        >
          {PULSE_SAMPLE_ITEMS.map((item, idx) => {
            const Icon = CATEGORY_ICON[item.category] ?? Inbox;
            return (
              <li
                key={item.headline}
                className="rounded-lg border bg-white p-3 dark:bg-card"
                data-testid={`item-pulse-sample-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand dark:bg-blue-900/40 dark:text-blue-400">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{item.headline}</p>
                        <p className="mt-0.5 text-sm text-muted-foreground">{item.whyItMatters}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                          {item.category}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`text-[10px] uppercase ${PRIORITY_STYLES[item.priority]}`}
                        >
                          {item.priority}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Upgrade CTA */}
        <div
          className="mt-5 rounded-lg border border-brand/20 bg-brand-muted/60 p-4 dark:border-blue-700/30 dark:bg-blue-900/20"
          data-testid="container-pulse-sample-upgrade"
          data-tour="pulse-sample-upgrade"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brand-accent">
              <Lock className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Upgrade to Professional to get your Daily Pulse
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Pulse reads your real inbox, reviews, financials, and bookings every
                morning and tells you exactly what needs your attention — in your own data.
              </p>
              <div className="mt-3">
                <Link
                  href="/app/account/billing"
                  className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-hover"
                  data-testid="link-pulse-sample-upgrade"
                >
                  Upgrade to Professional
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Compact upgrade nudge shown when the account's tier is not allowed access to Pulse AI (403). */
function PulseUpgradeCard() {
  return (
    <Card
      className="border-brand/20 bg-gradient-to-br from-brand-muted via-white to-white shadow-sm dark:border-blue-700/30 dark:from-blue-950/50 dark:via-card dark:to-card"
      data-testid="card-pulse-upgrade"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brand-accent">
            <Eye className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold tracking-wide text-brand dark:text-blue-400">PULSE</span>
              <Badge variant="outline" className="text-[10px] gap-1">
                <Lock className="h-2.5 w-2.5" />
                Upgrade required
              </Badge>
            </div>
            <p className="mt-1 text-sm text-foreground font-medium">
              Pulse AI is not available on your current plan
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Upgrade your plan to unlock AI-powered daily briefings, viewer-assigned
              task prioritization, and smart financial signals.
            </p>
            <div className="mt-3">
              <Link
                href="/app/account/billing"
                className="inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-blue-400 hover:underline"
                data-testid="link-pulse-upgrade"
              >
                View billing &amp; plans
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface VoiceResponse {
  script: string;
  /**
   * URL to the streaming MP3 endpoint. The browser fetches this lazily via
   * the <audio> element, which begins playback as soon as the first chunks
   * arrive (Task #2436) instead of waiting for the full clip.
   */
  audioUrl?: string;
  audioUnavailable?: true;
  briefingId: string;
}

type VoiceState = "idle" | "loading" | "playing" | "paused" | "error";

export function VoiceBriefingPlayer({ briefingGeneratedAt }: { briefingGeneratedAt: string | undefined }) {
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [script, setScript] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Track the briefingId for which we have audio loaded so we can
  // invalidate when the parent briefing refreshes.
  const loadedForRef = useRef<string | null>(null);

  // When the briefing refreshes, stop and clear the player so the next
  // click fetches fresh audio.
  useEffect(() => {
    if (
      briefingGeneratedAt &&
      loadedForRef.current &&
      loadedForRef.current !== briefingGeneratedAt
    ) {
      stopAndReset();
    }
  }, [briefingGeneratedAt]);

  // Clean up on unmount (e.g. navigating away).
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    };
  }, []);

  function stopAndReset() {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
    }
    setVoiceState("idle");
    setScript(null);
    setErrorMsg(null);
    setAudioUnavailable(false);
    loadedForRef.current = null;
  }

  async function handleTellItToMe() {
    if (voiceState === "playing") {
      audioRef.current?.pause();
      setVoiceState("paused");
      return;
    }
    if (voiceState === "paused") {
      audioRef.current?.play();
      setVoiceState("playing");
      return;
    }
    // idle or error → fetch + play
    setVoiceState("loading");
    setErrorMsg(null);
    setAudioUnavailable(false);
    try {
      const res = await fetch("/api/app/pulse/briefing/voice", {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as Record<string, unknown>;
        throw new Error((body.message as string) || `HTTP ${res.status}`);
      }
      const data = await res.json() as VoiceResponse;
      setScript(data.script || null);
      loadedForRef.current = data.briefingId;

      // If TTS was unavailable server-side, show transcript-only mode.
      if (data.audioUnavailable || !data.audioUrl) {
        setAudioUnavailable(true);
        setVoiceState("idle");
        return;
      }

      // Stream the MP3 directly via <audio src=...> — the browser starts
      // playback as soon as the first chunks arrive from the server, which
      // proxies ElevenLabs' streaming endpoint (Task #2436).
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const audio = audioRef.current;
      audio.preload = "auto";
      audio.src = data.audioUrl;
      audio.onended = () => setVoiceState("idle");
      audio.onerror = () => {
        setVoiceState("error");
        setErrorMsg("Audio playback failed. You can still read the transcript below.");
      };
      await audio.play();
      setVoiceState("playing");
    } catch (err) {
      setVoiceState("error");
      setErrorMsg((err as Error)?.message || "Something went wrong. Please retry.");
    }
  }

  function handleRestart() {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    audio.currentTime = 0;
    audio.play();
    setVoiceState("playing");
  }

  const isLoading = voiceState === "loading";
  const isPlaying = voiceState === "playing";
  const isPaused = voiceState === "paused";
  const hasAudio = isPlaying || isPaused;
  const hasError = voiceState === "error";
  const hasTranscript = !!script;

  return (
    <div className="mb-4" data-testid="container-voice-briefing">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Primary "Tell it to me" pill */}
        <button
          type="button"
          onClick={handleTellItToMe}
          disabled={isLoading}
          data-testid="button-pulse-tell-it-to-me"
          data-tour="pulse-tell-it-to-me"
          className={[
            "inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold shadow-sm transition-all",
            "border-2 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-1",
            isLoading
              ? "bg-brand/70 border-brand/70 text-white cursor-wait"
              : isPlaying
              ? "bg-brand border-brand-accent text-white"
              : isPaused
              ? "bg-white border-brand text-brand dark:bg-card dark:border-blue-500 dark:text-blue-400"
              : hasError
              ? "bg-white border-red-400 text-red-600 hover:bg-red-50 dark:bg-card dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
              : "bg-brand border-brand-accent text-white hover:bg-brand-hover hover:shadow-md",
          ].join(" ")}
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Preparing your briefing…
            </>
          ) : isPlaying ? (
            <>
              <Pause className="h-4 w-4" />
              Playing…
            </>
          ) : isPaused ? (
            <>
              <Play className="h-4 w-4" />
              Resume
            </>
          ) : hasError ? (
            <>
              <AlertCircle className="h-4 w-4" />
              Retry
            </>
          ) : (
            <>
              <Volume2 className="h-4 w-4 text-brand-accent" />
              Tell it to me
            </>
          )}
        </button>

        {/* Restart button — only when audio is loaded */}
        {hasAudio && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={handleRestart}
                  className="h-9 w-9 rounded-full border-brand/40 text-brand dark:border-blue-600/40 dark:text-blue-400"
                  data-testid="button-pulse-voice-restart"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Restart from beginning</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Stop/close button — when audio is loaded, transcript is showing, or audio-unavailable */}
        {(hasAudio || hasTranscript || hasError || audioUnavailable) && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={stopAndReset}
                  className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
                  data-testid="button-pulse-voice-stop"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Stop & close</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Error message */}
      {hasError && errorMsg && (
        <div
          className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700/40 dark:bg-red-900/25 dark:text-red-300"
          data-testid="text-pulse-voice-error"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Audio-unavailable notice — TTS failed server-side but script was generated */}
      {audioUnavailable && (
        <div
          className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-700/40 dark:bg-amber-900/25 dark:text-amber-300"
          data-testid="text-pulse-voice-audio-unavailable"
        >
          <Volume2 className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <span>Audio unavailable — read your briefing transcript below.</span>
        </div>
      )}

      {/* Transcript — shown while playing, paused, after audio error, or when audio is unavailable */}
      {hasTranscript && (
        <div
          className="mt-3 rounded-lg border border-brand/20 bg-brand-muted/60 px-4 py-3 dark:border-blue-700/30 dark:bg-blue-900/20"
          data-testid="container-pulse-voice-transcript"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-brand/60 mb-1.5 dark:text-blue-400/70">
            Transcript
          </p>
          <p
            className="text-sm text-foreground leading-relaxed"
            data-testid="text-pulse-voice-transcript"
          >
            {script}
          </p>
        </div>
      )}
    </div>
  );
}

export function PulseBriefing() {
  const { granted, loaded } = useBetaAccessState(PULSE_BETA_KEY);
  const [collapsed, setCollapsed] = useState(false);
  const { toast } = useToast();

  // Task #2426 — refresh cooldown state. When a 429 is returned the widget
  // reads retryAfterSeconds and disables the Refresh button with a countdown.
  const [refreshCooldownEndsAt, setRefreshCooldownEndsAt] = useState<number | null>(null);
  const [, forceCountdownRender] = useState(0);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!refreshCooldownEndsAt) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }
    countdownIntervalRef.current = setInterval(() => {
      if (Date.now() >= refreshCooldownEndsAt) {
        setRefreshCooldownEndsAt(null);
      } else {
        forceCountdownRender((n) => n + 1);
      }
    }, 1000);
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [refreshCooldownEndsAt]);

  const refreshCooldownSecondsLeft = refreshCooldownEndsAt
    ? Math.max(0, Math.ceil((refreshCooldownEndsAt - Date.now()) / 1000))
    : 0;

  useEffect(() => {
    try {
      sessionStorage.removeItem(DISMISS_KEY);
    } catch {
      /* sessionStorage unavailable */
    }
  }, []);

  const briefingQuery = useQuery<PulseBriefing>({
    queryKey: ["/api/app/pulse/briefing"],
    enabled: granted,
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => {
      // Don't retry 403 (tier gate) or 429 (cooldown) errors.
      if (is403Error(err)) return false;
      if (err instanceof Error && err.message.startsWith("429:")) return false;
      return count < 1;
    },
  });

  // Task #2426/2493 — detect tier-gate 403 so we can show the upgrade card.
  const isTierDenied = briefingQuery.isError && is403Error(briefingQuery.error);

  // Task #2431 — initialise the countdown from the server-persisted
  // cooldown on first load so reloading the page mid-cooldown doesn't
  // reset the timer to zero (which previously let hosts bypass the
  // tier-based refresh rate-limit).
  const initialCooldownSeconds = briefingQuery.data?.refreshCooldownSeconds;
  useEffect(() => {
    if (typeof initialCooldownSeconds !== "number" || initialCooldownSeconds <= 0) return;
    setRefreshCooldownEndsAt((current) => {
      const proposed = Date.now() + initialCooldownSeconds * 1000;
      // Only adopt the server value if no countdown is currently running,
      // or if the server's remaining time is meaningfully longer than what
      // we have locally (avoids "jumping forward" on every refetch).
      if (!current || proposed > current + 1000) return proposed;
      return current;
    });
  }, [initialCooldownSeconds]);

  const viewMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/pulse/view");
    },
  });

  useEffect(() => {
    if (granted && !briefingQuery.isLoading && briefingQuery.data) {
      viewMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granted, briefingQuery.isLoading, !!briefingQuery.data]);

  const refreshMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/pulse/briefing/refresh");
      return (await res.json()) as PulseBriefing & { refreshCooldownSeconds?: number };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/app/pulse/briefing"], data);
      // Task #2426 — server returns the just-armed cooldown so the Refresh
      // button disables immediately after a successful manual refresh.
      if (typeof data.refreshCooldownSeconds === "number" && data.refreshCooldownSeconds > 0) {
        setRefreshCooldownEndsAt(Date.now() + data.refreshCooldownSeconds * 1000);
      }
    },
    onError: (err) => {
      // Task #2426 — parse 429 cooldown and start countdown.
      const retryAfter = parseRetryAfterSeconds(err);
      if (retryAfter !== null && retryAfter > 0) {
        setRefreshCooldownEndsAt(Date.now() + retryAfter * 1000);
      }
    },
  });

  const feedbackMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (args: { itemKey: string; vote: "up" | "down"; note?: string }) => {
      await apiRequest("POST", "/api/app/pulse/feedback", args);
    },
  });

  const [downVoteDraft, setDownVoteDraft] = useState<
    { itemKey: string; headline: string; note: string } | null
  >(null);

  const [upVoteDraft, setUpVoteDraft] = useState<
    { itemKey: string; headline: string; note: string } | null
  >(null);

  const snoozeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (args: { itemKey: string; minutes: number }) => {
      await apiRequest("POST", "/api/app/pulse/snooze", args);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pulse/briefing"] });
    },
  });

  const dismissItemMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (itemKey: string) => {
      await apiRequest("POST", "/api/app/pulse/dismiss", { itemKey });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pulse/briefing"] });
    },
  });

  const confirmMemoryMutation = useMutation({
    mutationFn: async (suggestionId: number) => {
      const res = await apiRequest("POST", `/api/app/pulse/suggestions/${suggestionId}/confirm`, {});
      return (await res.json()) as { ok: boolean; target: "account" | "property"; propertyId: number | null };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pulse/briefing"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/ai-memory"] });
      if (data.propertyId != null) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/properties", data.propertyId, "ai-memory"] });
      }
      const kbHref = data.target === "account"
        ? "/app/account/knowledge-base"
        : data.propertyId
          ? `/app/properties/${data.propertyId}`
          : "/app/dashboard";
      toast({
        title: "Added to knowledge base",
        description: data.target === "account"
          ? "Saved under your account AI Memory section."
          : "Saved under this property's AI Memory section.",
        action: (
          <Link
            href={kbHref}
            className="text-xs font-medium text-brand dark:text-blue-400 hover:underline"
            data-testid="toast-link-view-kb"
          >
            View
          </Link>
        ) as any,
      });
    },
    onError: () => {
      toast({ title: "Couldn't save to knowledge base", variant: "destructive" });
    },
  });

  const dismissMemoryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (suggestionId: number) => {
      await apiRequest("POST", `/api/app/pulse/suggestions/${suggestionId}/dismiss`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pulse/briefing"] });
    },
  });

  const SNOOZE_OPTIONS: Array<{ label: string; minutes: number }> = [
    { label: "20 minutes", minutes: 20 },
    { label: "1 hour", minutes: 60 },
    { label: "4 hours", minutes: 4 * 60 },
    { label: "Tomorrow", minutes: 24 * 60 },
    { label: "Next week", minutes: 7 * 24 * 60 },
  ];

  const [feedbackByItem, setFeedbackByItem] = useState<Record<string, "up" | "down">>({});

  const LS_HIDE_KEY = "pulse-hidden-today";
  const todayStamp = () => new Date().toISOString().slice(0, 10);
  const signatureFor = (it: Pick<PulseItem, "category" | "headline">): string => {
    const cat = (it.category || "").toLowerCase().replace(/\s+/g, "-");
    const slug = (it.headline || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "item";
    return `${cat}::${slug}`;
  };
  type HiddenState = { keys: Set<string>; signatures: Set<string> };
  const [hidden, setHidden] = useState<HiddenState>(() => {
    try {
      const raw = localStorage.getItem(LS_HIDE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          date?: string;
          keys?: string[];
          signatures?: string[];
        };
        if (parsed?.date === todayStamp()) {
          return {
            keys: new Set(parsed.keys ?? []),
            signatures: new Set(parsed.signatures ?? []),
          };
        }
      }
    } catch {
      /* ignore */
    }
    return { keys: new Set<string>(), signatures: new Set<string>() };
  });
  const hideLocally = (it: Pick<PulseItem, "itemKey" | "category" | "headline">) => {
    setHidden((prev) => {
      const next: HiddenState = {
        keys: new Set(prev.keys),
        signatures: new Set(prev.signatures),
      };
      if (it.itemKey) next.keys.add(it.itemKey);
      next.signatures.add(signatureFor(it));
      try {
        localStorage.setItem(
          LS_HIDE_KEY,
          JSON.stringify({
            date: todayStamp(),
            keys: Array.from(next.keys),
            signatures: Array.from(next.signatures),
          }),
        );
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const briefing = briefingQuery.data;
  const items = useMemo(
    () =>
      (briefing?.items ?? []).filter((it) => {
        if (it.itemKey && hidden.keys.has(it.itemKey)) return false;
        if (hidden.signatures.has(signatureFor(it))) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [briefing, hidden],
  );

  if (!loaded || !granted) return null;

  // Task #2426/2493 — accounts denied by the tier lock see a compact upgrade nudge.
  if (isTierDenied) {
    return <PulseUpgradeCard />;
  }

  const refreshDisabled =
    refreshMutation.isPending ||
    briefingQuery.isLoading ||
    refreshCooldownSecondsLeft > 0;

  const refreshLabel =
    refreshCooldownSecondsLeft > 0
      ? `${refreshCooldownSecondsLeft}s`
      : undefined;

  return (
    <Card
      className="border-brand/20 bg-gradient-to-br from-brand-muted via-white to-white shadow-sm dark:border-blue-700/30 dark:from-blue-950/50 dark:via-card dark:to-card"
      data-testid="card-pulse-briefing"
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3" data-tour="pulse-heading">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brand-accent">
              <Eye className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold tracking-wide text-brand dark:text-blue-400">PULSE</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  the all-seeing eye of PadPulse
                </span>
                {briefing?.source === "fallback" && !briefingQuery.isLoading ? (
                  <Badge variant="outline" className="text-[10px]" data-testid="badge-pulse-fallback">
                    deterministic
                  </Badge>
                ) : null}
              </div>
              <p
                className="mt-1 text-base sm:text-lg font-medium text-foreground"
                data-testid="text-pulse-greeting"
              >
                {briefingQuery.isLoading
                  ? "Pulse is thinking…"
                  : briefing?.greeting ?? "Welcome — here's everything you need to know."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" data-tour="pulse-controls">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? "Show Pulse" : "Hide Pulse"}
              aria-pressed={!collapsed}
              data-testid="button-pulse-collapse"
            >
              <Eye className={`h-4 w-4 ${collapsed ? "text-muted-foreground" : "text-brand-accent"}`} />
            </Button>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => refreshMutation.mutate()}
                    disabled={refreshDisabled}
                    aria-label={
                      refreshCooldownSecondsLeft > 0
                        ? `Refresh available in ${refreshCooldownSecondsLeft}s`
                        : "Refresh Pulse briefing"
                    }
                    data-testid="button-pulse-refresh"
                    className="relative"
                  >
                    {refreshLabel ? (
                      <span className="text-[10px] font-mono leading-none">{refreshLabel}</span>
                    ) : (
                      <RefreshCw
                        className={`h-4 w-4 ${refreshMutation.isPending ? "animate-spin" : ""}`}
                      />
                    )}
                  </Button>
                </TooltipTrigger>
                {refreshCooldownSecondsLeft > 0 && (
                  <TooltipContent side="bottom" className="text-xs">
                    Refresh available in {refreshCooldownSecondsLeft}s
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <SettingsPopover />
          </div>
        </div>

        {!collapsed && (
          <div className="mt-0" data-testid="container-pulse-items">
            {briefingQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-3/4" />
              </div>
            ) : briefingQuery.isError ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-pulse-error"
              >
                Pulse couldn't load your briefing right now. Try refreshing in a moment.
              </p>
            ) : items.length === 0 ? (
              <p
                className="text-sm font-medium text-brand dark:text-blue-400"
                data-testid="text-pulse-empty"
              >
                You're all caught up — nice work.
              </p>
            ) : (
              <ul className="space-y-2">
                {items.map((item, idx) => {
                  // Task #2430 — assigned supply requests render with a Package
                  // icon instead of the default Tasks/ClipboardList so the host
                  // (or vendor-team-member viewer) can tell them apart at a glance.
                  const isAssignedSupply =
                    item.evidence?.assignedToViewer === true &&
                    item.evidence?.itemType === "supply";
                  const Icon = isAssignedSupply
                    ? Package
                    : (CATEGORY_ICON[item.category] ?? Inbox);
                  const currentVote = feedbackByItem[item.itemKey];
                  const isAssignedToViewer = item.evidence?.assignedToViewer === true;
                  return (
                    <li
                      key={item.itemKey || `${item.category}-${idx}`}
                      className={`group relative rounded-lg border bg-white p-3 pr-9 transition hover:border-brand/40 hover:shadow-sm dark:bg-card dark:hover:border-blue-600/40 ${isAssignedToViewer ? "border-brand/40 bg-brand-muted/30 dark:border-blue-600/40 dark:bg-blue-900/20" : ""}`}
                      data-testid={`item-pulse-${idx}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          hideLocally(item);
                          dismissItemMutation.mutate(item.itemKey);
                        }}
                        disabled={dismissItemMutation.isPending}
                        className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label="Dismiss"
                        data-testid={`button-pulse-dismiss-item-${idx}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand dark:bg-blue-900/40 dark:text-blue-400">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              {isAssignedToViewer && (
                                <span className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wider text-brand bg-brand-muted rounded px-1.5 py-0.5 dark:text-blue-400 dark:bg-blue-900/40">
                                  Assigned to you
                                </span>
                              )}
                              <p
                                className="text-sm font-semibold text-foreground"
                                data-testid={`text-pulse-headline-${idx}`}
                              >
                                {item.headline}
                              </p>
                              <p
                                className="mt-0.5 text-sm text-muted-foreground"
                                data-testid={`text-pulse-why-${idx}`}
                              >
                                {item.whyItMatters}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge
                                variant="outline"
                                className="text-[10px] uppercase tracking-wide"
                                data-testid={`badge-pulse-category-${idx}`}
                              >
                                {item.category}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-[10px] uppercase ${PRIORITY_STYLES[item.priority]}`}
                                data-testid={`badge-pulse-priority-${idx}`}
                              >
                                {item.priority}
                              </Badge>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-3 flex-wrap">
                            {item.category === "Memory" ? (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-7 px-3 bg-brand hover:bg-brand-hover text-white"
                                  disabled={confirmMemoryMutation.isPending || dismissMemoryMutation.isPending}
                                  onClick={() => {
                                    const sid = (item.evidence as any)?.suggestionId;
                                    if (typeof sid !== "number") return;
                                    hideLocally(item);
                                    confirmMemoryMutation.mutate(sid);
                                  }}
                                  data-testid={`button-pulse-memory-yes-${idx}`}
                                >
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                  Yes, add to KB
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-3"
                                  disabled={confirmMemoryMutation.isPending || dismissMemoryMutation.isPending}
                                  onClick={() => {
                                    const sid = (item.evidence as any)?.suggestionId;
                                    if (typeof sid !== "number") return;
                                    hideLocally(item);
                                    dismissMemoryMutation.mutate(sid);
                                  }}
                                  data-testid={`button-pulse-memory-no-${idx}`}
                                >
                                  No
                                </Button>
                                <Link
                                  href={item.deepLinkPath}
                                  className="text-xs text-muted-foreground hover:text-brand dark:hover:text-blue-400 hover:underline"
                                  data-testid={`link-pulse-memory-target-${idx}`}
                                >
                                  Open KB
                                </Link>
                                <WhyThisButton item={item} />
                              </>
                            ) : (
                            <>
                            <Link
                              href={item.deepLinkPath}
                              className="inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-blue-400 hover:underline"
                              data-testid={`link-pulse-cta-${idx}`}
                            >
                              Open
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                            {item.secondaryAction ? (
                              <Link
                                href={item.secondaryAction.path}
                                className="text-sm text-muted-foreground hover:text-brand dark:hover:text-blue-400 hover:underline"
                                data-testid={`link-pulse-secondary-${idx}`}
                              >
                                {item.secondaryAction.label}
                              </Link>
                            ) : null}
                            <WhyThisButton item={item} />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand dark:hover:text-blue-400"
                                  disabled={snoozeMutation.isPending}
                                  data-testid={`button-pulse-snooze-${idx}`}
                                >
                                  <Clock className="h-3.5 w-3.5" />
                                  Snooze
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-40">
                                {SNOOZE_OPTIONS.map((opt) => (
                                  <DropdownMenuItem
                                    key={opt.label}
                                    onSelect={() => {
                                      hideLocally(item);
                                      snoozeMutation.mutate({ itemKey: item.itemKey, minutes: opt.minutes });
                                    }}
                                    data-testid={`menuitem-pulse-snooze-${idx}-${opt.minutes}`}
                                  >
                                    {opt.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </>
                            )}
                          </div>
                          <div className="mt-2 flex items-center justify-end gap-2" {...(idx === 0 ? { "data-tour": "pulse-item-feedback" } : {})}>
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition ${
                                      currentVote === "up"
                                        ? "border-brand bg-brand-muted text-brand dark:border-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
                                        : "border-transparent text-muted-foreground hover:border-brand/40 hover:bg-brand-muted/50 hover:text-brand dark:hover:border-blue-600/40 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
                                    }`}
                                    onClick={() => {
                                      if (currentVote === "up") {
                                        setUpVoteDraft({
                                          itemKey: item.itemKey,
                                          headline: item.headline,
                                          note: "",
                                        });
                                        return;
                                      }
                                      setFeedbackByItem((prev) => ({
                                        ...prev,
                                        [item.itemKey]: "up",
                                      }));
                                      feedbackMutation.mutate({
                                        itemKey: item.itemKey,
                                        vote: "up",
                                      });
                                    }}
                                    aria-label="Useful"
                                    data-testid={`button-pulse-thumbs-up-${idx}`}
                                  >
                                    <ThumbsUp className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-xs">
                                  {currentVote === "up"
                                    ? "Marked useful — click again to add a note"
                                    : "Useful — click to give a quick thumbs-up"}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition ${
                                      currentVote === "down"
                                        ? "border-brand-amber bg-brand-accent-muted text-brand-amber dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
                                        : "border-transparent text-muted-foreground hover:border-brand-amber/40 hover:bg-brand-accent-muted/60 hover:text-brand-amber dark:hover:border-amber-600/40 dark:hover:bg-amber-900/20 dark:hover:text-amber-400"
                                    }`}
                                    onClick={() => {
                                      setDownVoteDraft({
                                        itemKey: item.itemKey,
                                        headline: item.headline,
                                        note: "",
                                      });
                                    }}
                                    aria-label="Not useful"
                                    data-testid={`button-pulse-thumbs-down-${idx}`}
                                  >
                                    <ThumbsDown className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-xs">
                                  Train me by giving me feedback on this message
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </CardContent>
      <Dialog
        open={downVoteDraft != null}
        onOpenChange={(open) => {
          if (!open && !feedbackMutation.isPending) setDownVoteDraft(null);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => {
            if (feedbackMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (feedbackMutation.isPending) e.preventDefault();
          }}
          data-testid="dialog-pulse-downvote"
        >
          <DialogHeader>
            <DialogTitle>Help train Pulse</DialogTitle>
            <DialogDescription>
              Please elaborate on why you're voting this down so I can use your
              feedback to get smarter about what to surface.
            </DialogDescription>
          </DialogHeader>
          {downVoteDraft && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Item:</span>{" "}
                {downVoteDraft.headline}
              </p>
              <Textarea
                autoFocus
                rows={4}
                placeholder="What's off about this? (e.g. wrong data, not actionable, already handled, irrelevant to me…)"
                value={downVoteDraft.note}
                onChange={(e) =>
                  setDownVoteDraft((prev) =>
                    prev ? { ...prev, note: e.target.value } : prev,
                  )
                }
                maxLength={2000}
                data-testid="input-pulse-downvote-note"
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={feedbackMutation.isPending}
              onClick={() => {
                if (!downVoteDraft) return;
                const { itemKey } = downVoteDraft;
                setFeedbackByItem((prev) => ({ ...prev, [itemKey]: "down" }));
                feedbackMutation.mutate(
                  { itemKey, vote: "down" },
                  { onSettled: () => setDownVoteDraft(null) },
                );
              }}
              data-testid="button-pulse-downvote-skip"
            >
              Skip
            </Button>
            <Button
              type="button"
              disabled={
                feedbackMutation.isPending ||
                !downVoteDraft?.note.trim()
              }
              onClick={() => {
                if (!downVoteDraft) return;
                const { itemKey, note } = downVoteDraft;
                setFeedbackByItem((prev) => ({ ...prev, [itemKey]: "down" }));
                feedbackMutation.mutate(
                  { itemKey, vote: "down", note: note.trim() },
                  { onSettled: () => setDownVoteDraft(null) },
                );
              }}
              data-testid="button-pulse-downvote-submit"
            >
              {feedbackMutation.isPending ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={upVoteDraft != null}
        onOpenChange={(open) => {
          if (!open && !feedbackMutation.isPending) setUpVoteDraft(null);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => {
            if (feedbackMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (feedbackMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (feedbackMutation.isPending) e.preventDefault();
          }}
          data-testid="dialog-pulse-upvote"
        >
          <DialogHeader>
            <DialogTitle>What was useful?</DialogTitle>
            <DialogDescription>
              Optionally tell me what made this insight helpful — I'll use your
              feedback to surface more of what matters to you.
            </DialogDescription>
          </DialogHeader>
          {upVoteDraft && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Item:</span>{" "}
                {upVoteDraft.headline}
              </p>
              <Textarea
                autoFocus
                rows={4}
                placeholder="What was helpful? (optional — e.g. great timing, accurate data, saved me time…)"
                value={upVoteDraft.note}
                onChange={(e) =>
                  setUpVoteDraft((prev) =>
                    prev ? { ...prev, note: e.target.value } : prev,
                  )
                }
                maxLength={2000}
                data-testid="input-pulse-upvote-note"
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={feedbackMutation.isPending}
              onClick={() => {
                if (!upVoteDraft) return;
                setUpVoteDraft(null);
              }}
              data-testid="button-pulse-upvote-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={feedbackMutation.isPending}
              onClick={() => {
                if (!upVoteDraft) return;
                const { itemKey, note } = upVoteDraft;
                const trimmed = note.trim();
                setFeedbackByItem((prev) => ({ ...prev, [itemKey]: "up" }));
                feedbackMutation.mutate(
                  { itemKey, vote: "up", ...(trimmed ? { note: trimmed } : {}) },
                  { onSettled: () => setUpVoteDraft(null) },
                );
              }}
              data-testid="button-pulse-upvote-submit"
            >
              {feedbackMutation.isPending ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PulseBriefing;
